import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { FaEye, FaPlus, FaTrash } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { buildChips, countActiveChips, resolveMapLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import SalarySheetDetail from "./SalarySheetDetail";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { fetchAccountingPermissions, ACCOUNTING_SO_CHI_SALARY } from "../../libs/customerCreatePermission";

const PAGE_SIZE = 20;

const STATUS_MAP = {
  DRAFT: { label: "Nháp", bg: "secondary" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  REFLECTED: { label: "Đã phản ánh", bg: "success" },
};

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const EMPTY_FILTERS = { period: "", status: "" };

const SalaryList = ({ embedded = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sheetIdFromUrl = searchParams.get("sheetId");

  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [newPeriod, setNewPeriod] = useState("");

  const [filterPeriod, setFilterPeriod] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_SO_CHI_SALARY);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [selectedSheetId, setSelectedSheetId] = useState(
    sheetIdFromUrl && !Number.isNaN(Number(sheetIdFromUrl)) ? Number(sheetIdFromUrl) : null
  );

  useEffect(() => {
    if (sheetIdFromUrl && !Number.isNaN(Number(sheetIdFromUrl))) {
      setSelectedSheetId(Number(sheetIdFromUrl));
    }
  }, [sheetIdFromUrl]);

  const chipDefs = useMemo(() => [
    { key: "period", label: "Kỳ lương", isActive: (v) => Boolean(v) },
    { key: "status", label: "Trạng thái", isActive: (v) => Boolean(v), formatValue: (v) => resolveMapLabel(v, STATUS_MAP) },
  ], []);

  const activeChips = useMemo(() => buildChips(appliedFilters, chipDefs), [appliedFilters, chipDefs]);

  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (filters.period) params.period = filters.period;
      if (filters.status) params.status = filters.status;
      const res = await axiosInstance.get("/api/accounting/salaries", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      toast.error("Không thể tải danh sách bảng lương");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  const openSheet = (id) => {
    setSelectedSheetId(id);
    const next = new URLSearchParams(searchParams);
    next.set("tab", next.get("tab") || "salary");
    next.set("sheetId", String(id));
    setSearchParams(next);
  };

  const closeSheet = () => {
    setSelectedSheetId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("sheetId");
    if (!next.get("tab")) next.set("tab", "salary");
    setSearchParams(next);
    fetchData(page, appliedFilters);
  };

  useEffect(() => {
    fetchData(0, EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
  };

  const handleSearch = () => applyFilters({ period: filterPeriod.trim(), status: filterStatus });

  const handleReset = () => {
    setFilterPeriod("");
    setFilterStatus("");
    applyFilters(EMPTY_FILTERS);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters, [key]: "" };
    setFilterPeriod(base.period || "");
    setFilterStatus(base.status || "");
    applyFilters(base);
  };

  const handleGenerate = async () => {
    if (!newPeriod.trim()) {
      toast.warn("Vui lòng nhập kỳ lương (VD: Tháng 06 năm 2026)");
      return;
    }
    const result = await Swal.fire({
      title: "Tạo bảng lương?",
      text: `Kỳ lương: ${newPeriod.trim()}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Tạo",
      cancelButtonText: "Huỷ",
    });
    if (!result.isConfirmed) return;
    setGenerating(true);
    try {
      const res = await axiosInstance.post("/api/accounting/salaries/generate", null, {
        params: { period: newPeriod.trim() },
      });
      const created = unwrap(res);
      toast.success("Tạo bảng lương thành công");
      setNewPeriod("");
      if (created?.id) {
        openSheet(created.id);
      } else {
        fetchData(0, appliedFilters);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Tạo bảng lương thất bại");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Xoá bảng lương?",
      text: row.period,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`/api/accounting/salaries/${row.id}`);
      if (res?.data && res.data.success === false) {
        toast.error(res.data.message || "Xoá thất bại");
        return;
      }
      toast.success("Đã xoá bảng lương");
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Xoá thất bại");
    }
  };


  if (selectedSheetId) {
    return (
      <div className={embedded ? "" : "acc-page"}>
        <SalarySheetDetail
          sheetId={selectedSheetId}
          onBack={closeSheet}
          perm={perm}
        />
      </div>
    );
  }

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title="Bảng lương theo kỳ"
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <div className="d-flex align-items-center gap-2">
                {perm.canCreate && (
                  <>
                    <Form.Control
                      size="sm"
                      style={{ minWidth: 180 }}
                      placeholder="Kỳ lương mới, VD: Tháng 06 năm 2026"
                      value={newPeriod}
                      onChange={(e) => setNewPeriod(e.target.value)}
                    />
                    <Button variant="primary" size="sm" onClick={handleGenerate} disabled={generating}>
                      {generating ? <Spinner animation="border" size="sm" /> : <><FaPlus className="me-1" /> Tạo bảng lương</>}
                    </Button>
                  </>
                )}
              </div>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Kỳ lương (lọc)</Form.Label>
                <Form.Control size="sm" placeholder="Nhập để lọc..." value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} />
              </Col>
              <Col>
                <Form.Label className="small mb-1">Trạng thái</Form.Label>
                <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">Tất cả</option>
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters chips={activeChips} onRemove={handleRemoveChip} onClearAll={handleReset} />
        </Card.Body>
      </Card>

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Chưa có bảng lương nào</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <table className="table table-striped table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>STT</th>
                      <th>Kỳ lương</th>
                      <th>Tiêu đề</th>
                      <th className="text-center">Số NV</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="acc-col-hide-sm">Ngày tạo</th>
                      <th className="acc-col-hide-sm">Ngày duyệt</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={r.id}>
                        <td>{page * PAGE_SIZE + idx + 1}</td>
                        <td className="fw-semibold">{r.period}</td>
                        <td>{r.title}</td>
                        <td className="text-center">{r.lineCount ?? 0}</td>
                        <td className="text-center">
                          <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                            {STATUS_MAP[r.status]?.label || r.status}
                          </Badge>
                        </td>
                        <td className="acc-col-hide-sm">{r.createdAt}</td>
                        <td className="acc-col-hide-sm">{r.approvedAt || "-"}</td>
                        <td className="text-center">
                          <div className="acc-action-btns">
                            <Button variant="outline-primary" size="sm" title="Xem / Sửa" onClick={() => openSheet(r.id)}>
                              <FaEye />
                            </Button>
                            {perm.canDelete && r.status !== "REFLECTED" && (
                              <Button variant="outline-danger" size="sm" title="Xoá" onClick={() => handleDelete(r)}>
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">Tổng: {totalElements} bảng lương</small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => fetchData(p - 1)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default SalaryList;
